import { useEffect, useState } from "react";
import axios from "axios";
import { useLang } from "@/context/LangContext";
import { activities } from "@/data/activities";
import { Star, MessageSquarePlus } from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ACTIVITY_LABEL = {
  barrancos: { es: "Barrancos", en: "Canyoning" },
  cuevas: { es: "Cuevas", en: "Caving" },
  ferratas: { es: "Vías Ferratas", en: "Via Ferratas" },
  escalada: { es: "Escalada", en: "Climbing" },
};

export default function Testimonials() {
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);

  const fetchAll = async () => {
    try {
      const { data } = await axios.get(`${API}/testimonials`);
      setItems(data || []);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const onPublished = (newItem) => {
    setItems((prev) => [newItem, ...prev]);
    setShowForm(false);
  };

  return (
    <section id="resenas" className="bg-[#1A1B1A] py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase font-bold text-[#F26419] mb-4">
              {t("testimonials.kicker")}
            </p>
            <h2 className="font-display font-black text-4xl sm:text-5xl md:text-6xl leading-[0.95] tracking-tighter text-white max-w-3xl">
              {t("testimonials.title")}
            </h2>
          </div>
          <button
            data-testid="testimonials-toggle-form"
            onClick={() => setShowForm((v) => !v)}
            className="inline-flex items-center gap-2 bg-[#F26419] hover:bg-[#D95512] text-white font-bold tracking-[0.1em] uppercase px-6 py-3 text-sm transition-colors"
          >
            <MessageSquarePlus className="w-4 h-4" />
            {t("testimonials.form.title")}
          </button>
        </div>

        {showForm && <ReviewForm onPublished={onPublished} />}

        {items.length === 0 ? (
          <p className="text-white/60 text-center py-12 italic">
            {t("testimonials.empty")}
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
            {items.map((q) => (
              <article
                key={q.id}
                className="bg-[#F9F8F6] text-[#1A1B1A] p-7 border-2 border-[#F26419] flex flex-col"
              >
                <div className="flex items-center gap-1 mb-4">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                      key={i}
                      className={`w-4 h-4 ${
                        i < (q.rating || 5)
                          ? "fill-[#F26419] text-[#F26419]"
                          : "text-[#D1D5DB]"
                      }`}
                    />
                  ))}
                </div>
                <p className="font-display text-xl leading-snug flex-1">
                  &ldquo;{q.message}&rdquo;
                </p>
                <div className="mt-5 pt-4 border-t border-[#1A1B1A]/10">
                  <p className="text-sm font-bold tracking-wider text-[#2D5A3C]">
                    {q.name}
                    {q.location ? ` · ${q.location}` : ""}
                  </p>
                  {q.activity && ACTIVITY_LABEL[q.activity] && (
                    <p className="text-[10px] tracking-[0.2em] uppercase text-[#5C635A] mt-1">
                      {ACTIVITY_LABEL[q.activity][lang]}
                    </p>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function ReviewForm({ onPublished }) {
  const { t, lang } = useLang();
  const [form, setForm] = useState({
    name: "",
    location: "",
    activity: "",
    rating: 5,
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || form.message.length < 10) {
      toast.error(
        lang === "es"
          ? "Nombre y mensaje (mínimo 10 caracteres) son obligatorios"
          : "Name and message (min 10 chars) are required"
      );
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/testimonials`, form);
      toast.success(t("testimonials.success"));
      onPublished(data);
    } catch (err) {
      console.error(err);
      toast.error(t("testimonials.error"));
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-0 border-b-2 border-white/30 rounded-none px-0 py-3 text-base text-white placeholder:text-white/40 focus:outline-none focus:border-[#F26419] transition-colors";
  const labelCls =
    "block text-xs tracking-[0.2em] uppercase font-bold text-white/60 mb-2";

  return (
    <form
      onSubmit={submit}
      data-testid="testimonial-form"
      className="bg-[#2D5A3C] border-2 border-[#F26419] p-6 md:p-10 mb-10"
    >
      <h3 className="font-display font-black text-2xl md:text-3xl text-white">
        {t("testimonials.form.title")}
      </h3>
      <p className="text-white/70 mt-2 mb-8 text-sm">{t("testimonials.form.intro")}</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className={labelCls}>{t("testimonials.label.name")} *</label>
          <input
            data-testid="testimonial-input-name"
            type="text"
            required
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls}>{t("testimonials.label.location")}</label>
          <input
            data-testid="testimonial-input-location"
            type="text"
            value={form.location}
            onChange={(e) => update("location", e.target.value)}
            className={inputCls}
            placeholder="Bilbao, Santander..."
          />
        </div>

        <div>
          <label className={labelCls}>{t("testimonials.label.activity")}</label>
          <Select value={form.activity} onValueChange={(v) => update("activity", v)}>
            <SelectTrigger
              data-testid="testimonial-select-activity"
              className="w-full bg-transparent border-0 border-b-2 border-white/30 rounded-none px-0 py-3 h-auto text-base text-white focus:ring-0 focus:border-[#F26419]"
            >
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent>
              {activities.map((a) => (
                <SelectItem key={a.slug} value={a.slug}>
                  {a.title[lang]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className={labelCls}>{t("testimonials.label.rating")}</label>
          <div className="flex items-center gap-1 pt-3">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                data-testid={`testimonial-rating-${n}`}
                onClick={() => update("rating", n)}
                className="p-1"
                aria-label={`rating ${n}`}
              >
                <Star
                  className={`w-7 h-7 transition-colors ${
                    n <= form.rating
                      ? "fill-[#F26419] text-[#F26419]"
                      : "text-white/30"
                  }`}
                />
              </button>
            ))}
          </div>
        </div>

        <div className="md:col-span-2">
          <label className={labelCls}>{t("testimonials.label.message")} *</label>
          <textarea
            data-testid="testimonial-input-message"
            required
            rows={3}
            minLength={10}
            maxLength={600}
            value={form.message}
            onChange={(e) => update("message", e.target.value)}
            className={`${inputCls} resize-none`}
            placeholder={t("testimonials.placeholder.message")}
          />
        </div>
      </div>

      <button
        type="submit"
        data-testid="testimonial-submit"
        disabled={loading}
        className="mt-8 bg-[#F26419] hover:bg-[#D95512] disabled:opacity-60 text-white font-bold tracking-[0.1em] uppercase px-10 py-4 text-sm transition-colors"
      >
        {loading ? t("testimonials.submitting") : t("testimonials.submit")}
      </button>
    </form>
  );
}
